import React from 'react';

import { usePlanRowColumnsClassName } from './PlanGridSection';
import { usePlansPageClassName } from './usePlansPageClassName';

const cardBorderClassName =
  'rounded-lg border border-solid border-non-hover-border bg-white';

/** Lets the summary silhouette's tracks switch on its own width, as the card does. */
const containerClassName = '@container';

/** A single placeholder bar, matching the app's other loading states. */
export const SkeletonBar: React.FC<{ className: string }> = ({ className }) => (
  <div className={`animate-pulse rounded bg-non-hover-border ${className}`} />
);

/**
 * The current-plan summary's silhouette — three stacked rows. Rendered both
 * by the card itself, while its own balance query is in flight, and by the
 * page skeleton above it.
 */
export const CurrentPlanSummarySkeleton: React.FC = () => (
  <div className={`${containerClassName} ${cardBorderClassName}`}>
    {[0, 1, 2].map((row) => (
      <div
        key={row}
        className={`flex flex-col gap-3 p-5 ${
          row > 0
            ? 'border-0 border-t border-solid border-non-hover-border'
            : ''
        }`}
      >
        <SkeletonBar className="h-4 w-28" />
        <SkeletonBar className="h-7 w-40" />
        {row === 1 && <SkeletonBar className="h-1.5 w-full" />}
      </div>
    ))}
  </div>
);

/**
 * One plan card's silhouette: the header block down to its divider, then a few
 * feature groups. Sized to the real card so the grid doesn't jump when the
 * data lands.
 */
export const PlanCardSkeleton: React.FC = () => (
  <div className={`flex min-w-0 flex-1 flex-col ${cardBorderClassName}`}>
    <div className="flex flex-col gap-4 border-0 border-b border-solid border-divider-card p-5">
      <div className="flex flex-col gap-2">
        <SkeletonBar className="h-5 w-28" />
        <SkeletonBar className="h-4 w-40" />
      </div>
      <div className="flex flex-col gap-2">
        <SkeletonBar className="h-4 w-16" />
        <SkeletonBar className="h-8 w-32" />
      </div>
      <SkeletonBar className="h-8 w-full" />
    </div>
    <div className="flex flex-col gap-4 p-5">
      {[0, 1, 2].map((group) => (
        <div key={group} className="flex flex-col gap-2">
          <SkeletonBar className="h-4 w-24" />
          <SkeletonBar className="h-4 w-4/5" />
          <SkeletonBar className="h-4 w-3/5" />
        </div>
      ))}
    </div>
  </div>
);

export const PlanGridSkeleton: React.FC<{ cardCount?: number }> = ({
  cardCount = 3,
}) => {
  const planRowClassName = usePlanRowColumnsClassName();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <SkeletonBar className="h-6 w-40" />
        <SkeletonBar className="h-4 w-80" />
      </div>
      {/* The real row's own column rule (see PlanGridSection), so the layout
        doesn't jump when the data lands. No subgrid: silhouettes carry no
        content to align. */}
      <div className={planRowClassName}>
        {Array.from({ length: cardCount }, (_, index) => (
          <PlanCardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
};

/**
 * The whole page while its plan, price and balance data resolve. The page
 * reads several sources that land at different times, and rendering before
 * they do shows real-looking but wrong figures — a Studio layout for a
 * Classic workspace, a monthly toggle on an annual plan, an empty price —
 * that then rewrite themselves under the reader.
 */
export const PlansPageSkeleton: React.FC = () => {
  const pageClassName = usePlansPageClassName();

  return (
    <div className={pageClassName}>
      <CurrentPlanSummarySkeleton />
      <PlanGridSkeleton />
    </div>
  );
};
